// Cloud Control canister type. desired is what the cloud last asked the machine
// to use. reported is what the machine says it applied. A null value is unknown:
// the page must not assume Componenti.

export const CANISTER_TYPES = ['Componenti', 'Azkoyen'];

export type CanisterTypeName = 'Componenti' | 'Azkoyen';

export interface CanisterTypeState {
	machine_id: string;
	serial_no: string;
	desired: string | null;
	reported: string | null;
	pending: boolean;
}

export interface CanisterTypeFieldError {
	field: string;
	message: string;
}

export interface CanisterTypeRequestError {
	status: number;
	message: string;
	details: CanisterTypeFieldError[];
}

export function displayCanisterType(value: string | null | undefined): string {
	if (value === null || value === undefined || String(value).trim() === '') {
		return 'Unknown';
	}
	return String(value);
}

export function isCanisterTypeName(value: string | null | undefined): value is CanisterTypeName {
	return value === 'Componenti' || value === 'Azkoyen';
}

// The shadow already holds this request. Sending it again does not change the machine.
export function isNoOpSelection(state: CanisterTypeState, selection: string | null): boolean {
	return !!state && selection != null && selection === state.desired;
}

export function canSendCanisterType(state: CanisterTypeState | null, selection: string | null, locked: boolean): boolean {
	if (!state || locked || !isCanisterTypeName(selection)) {
		return false;
	}
	return !isNoOpSelection(state, selection);
}

export function isAwaitingMachine(state: CanisterTypeState): boolean {
	if (!state) {
		return false;
	}
	if (state.pending) {
		return true;
	}
	return state.desired != null && state.desired !== state.reported;
}

export function isApplied(state: CanisterTypeState): boolean {
	return !!state
		&& !isAwaitingMachine(state)
		&& state.desired != null
		&& state.desired === state.reported;
}

export function canisterStateLabel(state: CanisterTypeState): string {
	if (isAwaitingMachine(state)) {
		return 'Pending';
	}
	if (isApplied(state)) {
		return 'Applied';
	}
	return 'No request yet';
}

// machine_table.canister_type from the registration lookup, which answers with
// rows. It is what the machine was registered as, not what it reports.
export function registeredCanisterType(rows: any): CanisterTypeName | null {
	const row = Array.isArray(rows) ? rows[0] : rows;
	const value = row && typeof row.canister_type === 'string' ? row.canister_type.trim() : '';
	return isCanisterTypeName(value) ? value : null;
}

export type MachineTypeSource = 'reported' | 'registered' | 'unknown';

// What to show as the type on the machine: the reported value when there is
// one, else the registration record's, which can only be assumed.
export function machineTypeSource(reported: string | null, registered: string | null): MachineTypeSource {
	if (isCanisterTypeName(reported)) {
		return 'reported';
	}
	return isCanisterTypeName(registered) ? 'registered' : 'unknown';
}

export function currentTypeText(reported: string | null, registered: string | null): string {
	const source = machineTypeSource(reported, registered);
	if (source === 'reported') {
		return 'The machine currently reports ' + reported + '.';
	}
	if (source === 'registered') {
		return 'The machine has not reported a type. It is registered as ' + registered + '.';
	}
	return 'The machine has not reported a type.';
}

export function lastConfirmedText(reported: string | null): string {
	if (isCanisterTypeName(reported)) {
		return 'Last confirmed: ' + reported + '.';
	}
	return 'The machine has not confirmed a canister type yet.';
}

export function waitingDetail(reported: string | null): string {
	return 'The machine applies it the next time it is online, then reports back. ' + lastConfirmedText(reported);
}

// Polling has stopped. The request stays in the shadow, so the machine can still
// apply it later; checking again is all that is left to do.
export function notConfirmedDetail(desired: string | null, reported: string | null): string {
	return 'The machine has not reported ' + displayCanisterType(desired)
		+ ' yet. It may be offline, or its software may not apply canister type changes yet. The request stays in place until it does. '
		+ lastConfirmedText(reported);
}

export function normalizeCanisterTypeError(error: any): CanisterTypeRequestError {
	const response = error && error.response;
	const data = response && response.data && typeof response.data === 'object' ? response.data : null;
	const status = (response && response.status) || (data && data.status) || 0;
	const payload = data && data.error;
	const details = payload && typeof payload === 'object' && Array.isArray(payload.details)
		? payload.details
		: [];
	const message = (data && data.message)
		|| (typeof payload === 'string' ? payload : '')
		|| (error && error.message)
		|| 'Something went wrong.';
	return { status, message, details };
}

export function canisterTypeErrorMessage(error: CanisterTypeRequestError, action: 'load' | 'change'): string {
	if (error.status === 404) {
		return 'This serial number is not a registered machine.';
	}
	if (error.status === 502) {
		return 'The cloud could not update the machine. Try again.';
	}
	if (error.status === 400) {
		const detail = (error.details || [])
			.map(item => item && item.message)
			.filter(message => !!message)
			.join(' ');
		return detail || 'That request was not accepted. Check the canister type and try again.';
	}
	if (error.status === 401) {
		return 'Only a super admin can change canister type.';
	}
	if (error.status === 500) {
		return 'Something went wrong on the server. Try again.';
	}
	if (!error.status) {
		return action === 'load'
			? 'Could not reach the cloud to load canister type. Try again.'
			: 'Could not reach the cloud. The change was not sent. Try again.';
	}
	return error.message || 'Something went wrong. Try again.';
}
